import { DateTime, Duration } from "luxon";
import { API_DOMAIN, PROTOCOL } from "../globals";
import db from "../database/database";
import { infoLog } from "./logger";
import type { LegacyProviderConfigDto } from "@docket/consumer-app-client-typescript-axios";

// Oh god why
// https://stackoverflow.com/questions/201323/how-can-i-validate-an-email-address-using-a-regular-expression
// http://emailregex.com/
export const emailRegex =
  // Runs only on the user's own input, in their own browser, so ReDoS isn't a concern.
  // eslint-disable-next-line no-control-regex, security/detect-unsafe-regex
  /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/;

export const pdfViewer = "view_pdf?name=";
//let embed = "https://docs.google.com/gview?embedded=true&url="

export const userPinAttempts = "userPinAttempts";
export const userPinAllowedDate = "userPinAllowedDate";

export const androidImmunizationsFaqPdfURL = `${PROTOCOL}://${API_DOMAIN}/${pdfViewer}Docket_Immunizations_FAQs_Overview.pdf`;
export const androidImmunizationsMultiMatchPdfURL = `${PROTOCOL}://${API_DOMAIN}/${pdfViewer}Docket_Immunizations_MULTI_MATCH_Help.pdf`;
export const androidImmunizationsNoMatchPdfURL = `${PROTOCOL}://${API_DOMAIN}/${pdfViewer}Docket_Immunizations_NO_MATCH_Help.pdf`;
export const androidImmunizationsUtahPinHelpPdfURL = `${PROTOCOL}://${API_DOMAIN}/${pdfViewer}Docket_Utah_PIN_Help.pdf`;
export const androidImmunizationsNJPrivacyPdfURL = `${PROTOCOL}://${API_DOMAIN}/${pdfViewer}NJ_Privacy_Notice.pdf`;

export const immunizationsFaqPdfURL = `${PROTOCOL}://${API_DOMAIN}/Docket_Immunizations_FAQs_Overview.pdf`;
export const immunizationsMultiMatchPdfURL = `${PROTOCOL}://${API_DOMAIN}/Docket_Immunizations_MULTI_MATCH_Help.pdf`;
export const immunizationsNoMatchPdfURL = `${PROTOCOL}://${API_DOMAIN}/Docket_Immunizations_NO_MATCH_Help.pdf`;
export const immunizationsUtahPinHelpPdfURL = `${PROTOCOL}://${API_DOMAIN}/Docket_Utah_PIN_Help.pdf`;
export const immunizationsNJPrivacyPdfURL = `${PROTOCOL}://${API_DOMAIN}/NJ_Privacy_Notice.pdf`;
export const docketPrivacyPdfURL = `${PROTOCOL}://${API_DOMAIN}/privacy_policy`;
export const docketTOSPdfURL = `${PROTOCOL}://${API_DOMAIN}/terms_of_use`;

export const virusFullNames = [
  "COVID-19",
  "Hepatitis B",
  "Rotavirus",
  "Diphtheria, Tetanus, Pertussis",
  "Haemophilus influenzae B",
  "Pneumococcal",
  "Polio",
  "Influenza",
  "Measles, Mumps, Rubella",
  "Varicella (Chickenpox)",
  "Hepatitis A",
  "Human Papillomavirus (HPV)",
  "Meningococcal",
  "Meningococcal B",
  "Zoster (Shingles)",
  "Yellow Fever",
  "Typhoid",
  "Monkeypox",
];

// The order the avatar sheet shows them in, grouped by color: red, yellow, green, blue, purple.
const avatarOrder = [
  // Red
  110, 114, 117, 121, 97, 98, 99, 100, 101, 102, 103, 104, 105, 106,
  // Yellow
  122,
  // Green
  108, 112, 116, 119, 77, 78, 79, 80, 81, 82, 83, 84, 85, 86,
  // Blue
  107, 111, 115, 118, 120, 67, 68, 69, 70, 71, 72, 73, 74, 75, 76,
  // Purple
  109, 113, 87, 88, 89, 90, 91, 92, 93, 94, 95, 96,
] as const;

// Records store the id, so never reuse or renumber one, even for an avatar we removed. The next
// free id is 123. `satisfies` fails the type check unless every id here is in avatarOrder, and back.
const avatarImages = {
  67: "/assets/images/JL_Avatar_1.png",
  68: "/assets/images/JL_Avatar_2.png",
  69: "/assets/images/JL_Avatar_3.png",
  70: "/assets/images/JL_Avatar_4.png",
  71: "/assets/images/JL_Avatar_5.png",
  72: "/assets/images/JL_Avatar_6.png",
  73: "/assets/images/JL_Avatar_7.png",
  74: "/assets/images/JL_Avatar_8.png",
  75: "/assets/images/JL_Avatar_9.png",
  76: "/assets/images/JL_Avatar_10.png",
  77: "/assets/images/JL_Avatar_11.png",
  78: "/assets/images/JL_Avatar_12.png",
  79: "/assets/images/JL_Avatar_13.png",
  80: "/assets/images/JL_Avatar_14.png",
  81: "/assets/images/JL_Avatar_15.png",
  82: "/assets/images/JL_Avatar_16.png",
  83: "/assets/images/JL_Avatar_17.png",
  84: "/assets/images/JL_Avatar_18.png",
  85: "/assets/images/JL_Avatar_19.png",
  86: "/assets/images/JL_Avatar_20.png",
  87: "/assets/images/JL_Avatar_21.png",
  88: "/assets/images/JL_Avatar_22.png",
  89: "/assets/images/JL_Avatar_23.png",
  90: "/assets/images/JL_Avatar_24.png",
  91: "/assets/images/JL_Avatar_25.png",
  92: "/assets/images/JL_Avatar_26.png",
  93: "/assets/images/JL_Avatar_27.png",
  94: "/assets/images/JL_Avatar_28.png",
  95: "/assets/images/JL_Avatar_29.png",
  96: "/assets/images/JL_Avatar_30.png",
  97: "/assets/images/JL_Avatar_31.png",
  98: "/assets/images/JL_Avatar_32.png",
  99: "/assets/images/JL_Avatar_33.png",
  100: "/assets/images/JL_Avatar_34.png",
  101: "/assets/images/JL_Avatar_35.png",
  102: "/assets/images/JL_Avatar_36.png",
  103: "/assets/images/JL_Avatar_37.png",
  104: "/assets/images/JL_Avatar_38.png",
  105: "/assets/images/JL_Avatar_39.png",
  106: "/assets/images/JL_Avatar_40.png",
  107: "/assets/images/JL_Dragon_BB.png",
  108: "/assets/images/JL_Dragon_BG.png",
  109: "/assets/images/JL_Dragon_BP.png",
  110: "/assets/images/JL_Dragon_BR.png",
  111: "/assets/images/JL_Dragon_YB.png",
  112: "/assets/images/JL_Dragon_YG.png",
  113: "/assets/images/JL_Dragon_YP.png",
  114: "/assets/images/JL_Dragon_YR.png",
  115: "/assets/images/Plaid_BB.png",
  116: "/assets/images/Plaid_GG.png",
  117: "/assets/images/Plaid_RR.png",
  118: "/assets/images/Virus_BB.png",
  119: "/assets/images/Virus_GG.png",
  120: "/assets/images/Virus_LBB.png",
  121: "/assets/images/Virus_RR.png",
  122: "/assets/images/Virus_YR.png",
} as const satisfies Record<(typeof avatarOrder)[number], string>;

export const avatars: Map<number, string> = new Map<number, string>(
  // eslint-disable-next-line security/detect-object-injection -- id is one of avatarOrder's literals
  avatarOrder.map((id) => [id, avatarImages[id]]),
);

export const setPinAllowedDate = async (date: string) => {
  await db().setItem(userPinAllowedDate, date);
};

export const getPinAllowedDate = async () => {
  return (await db().getItem(userPinAllowedDate)) ?? null;
};

export const setPinAttempts = async (reset: boolean) => {
  const attempts = await getPinAttempts();
  infoLog(`pin attempts ${attempts}`);
  await db().setItem(userPinAttempts, reset ? 0 : attempts + 1);
};

export const getPinAttempts = async () => {
  const value: number | null = await db().getItem(userPinAttempts);
  const parsed = value ? value : 0;
  return parsed;
};

export const sortApiConfigAscending = (
  config: LegacyProviderConfigDto[],
): LegacyProviderConfigDto[] => {
  if (config) {
    return config.sort(function (a, b) {
      const textA = a.iz_provider.destination_id.toUpperCase();
      const textB = b.iz_provider.destination_id.toUpperCase();
      return textA < textB ? -1 : textA > textB ? 1 : 0;
    });
  }
  return [] as LegacyProviderConfigDto[];
};

export const formatNumber = (pn: string) => {
  if (pn.length === 10) {
    return `(${pn.slice(0, 3)}) ${pn.slice(3, 6)}-${pn.slice(6)}`;
  }
  // potentially malformed or intl number
  return pn;
};

export const pwdCheck = (pwd: string) => {
  if (pwd.length < 8) {
    return "At least eight (8) characters long, please!";
  } else if (pwd.length > 50) {
    return "No more than fifty (50) characters, please!";
  } else {
    let totalMatches = 0;
    totalMatches += /[a-z]/.test(pwd) ? 1 : 0; // lower
    totalMatches += /[A-Z]/.test(pwd) ? 1 : 0; // upper
    totalMatches += /[0-9]/.test(pwd) ? 1 : 0; // number
    totalMatches += /[^a-zA-Z0-9]/.test(pwd) ? 1 : 0; // symbol
    if (totalMatches >= 2) {
      return "";
    }
    return "Passwords must be 8+ characters and contain at least 1: uppercase, number, or special character!";
  }
};

// Luxon doesn't really have a way to parse multiple formats at once when
// we don't know the format ahead of time and the PWA seems to swap between
// ISO and locale format depending on input source.
export const getDateTime = (date: string): DateTime<true> | DateTime<false> => {
  let dateTime = DateTime.fromISO(date);
  if (!dateTime.isValid) {
    dateTime = DateTime.fromFormat(date, "MM/dd/yyyy");

    if (!dateTime.isValid) {
      dateTime = DateTime.fromFormat(date, "M/dd/yyyy");
    }

    if (!dateTime.isValid) {
      dateTime = DateTime.fromFormat(date, "M/d/yyyy");
    }
  }

  return dateTime;
};

export const readableDate = (date: number | Date | string) => {
  if (date instanceof Date) {
    return DateTime.fromJSDate(date).toISODate() ?? "";
  } else if (typeof date === "number") {
    return DateTime.fromMillis(date).toISODate() ?? "";
  }
  return getDateTime(date).toISODate() ?? "";
};

export const getWaitTimeText = (waitTime: number) => {
  if (waitTime > 600 || waitTime < 1) {
    return "...";
  }
  return Duration.fromObject({
    seconds: waitTime < 60 ? waitTime : undefined,
    minutes: waitTime >= 60 && waitTime <= 600 ? Math.ceil(waitTime / 60) : undefined,
  }).toHuman();
};
